/**
 * M11 — Catálogo de líneas de servicio en el cliente.
 *
 * useServiceLines() carga GET /service-lines UNA vez por sesión (caché de
 * módulo compartida entre componentes) y expone:
 *  - lines: solo activas, ordenadas (para selectores).
 *  - allLines: todas (para resolver nombres de líneas retiradas en histórico).
 *  - nameOf(code): nombre humano con fallback al label legado o al propio código.
 * Tras crear/editar líneas en Ajustes, llama a invalidateServiceLinesCache().
 */

import { useEffect, useState } from 'react'
import { api } from './api'
import { ServiceLineLabel, type ServiceLineDefDto, type ServiceLineListResponse } from '@toga/shared'

let cache: ServiceLineDefDto[] | null = null
let inflight: Promise<ServiceLineDefDto[]> | null = null

async function fetchAll(): Promise<ServiceLineDefDto[]> {
  if (cache) return cache
  if (!inflight) {
    inflight = api
      .get<ServiceLineListResponse>('/service-lines')
      .then((r) => {
        cache = r.items
        return r.items
      })
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}

export function invalidateServiceLinesCache() {
  cache = null
}

export function useServiceLines() {
  const [allLines, setAllLines] = useState<ServiceLineDefDto[]>(cache ?? [])
  const [loading, setLoading] = useState(cache === null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    void fetchAll()
      .then((items) => {
        if (!cancelled) {
          setAllLines(items)
          setLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true)
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const lines = allLines.filter((l) => l.active)
  const nameOf = (code: string | null | undefined): string => {
    if (!code) return ''
    return allLines.find((l) => l.code === code)?.name ?? ServiceLineLabel[code] ?? code
  }

  return { lines, allLines, nameOf, loading, failed }
}
